import React from "react"
import { ConfigurablePropsFromDescription, useWidgetId } from "../../../helpers/widget"
import PublishUtils from "../../../helpers/PublishUtils"
import { EMPTY_IFC_TO_LD_CONFIG, IfcToLdConfig } from "./shared/ifc-to-ld-config"
import { AmbientOcclusionConfig } from "./shared/ambient-occlusion-config"
import { Override } from "../../../utils/typescript"
import { CodeEditor } from "../../editor/code-editor"
import { iframeConnectionsByWidgetId } from "./ifc-viewer"
import { Button } from "../../ui-primitives/button"
import { GridHelp } from "./grid-help"
import { AmbientOcclusionHelp } from "./ambient-occlusion-help"
import { SKYBOX_PRESETS } from "./shared/skybox-config"

export const configOptions = [
  {
    name: "query",
    type: 'yasgui',
    label: (
      <div>
        Parameters: <code>?guid</code>,{' '}
                    <code>?hidden</code> (boolean),{' '}
                    <code>?tag</code>,{' '}
                    <code>?color</code> (css color)
      </div>
    )
  },
  {
    name: 'viewerOnly',
    type: 'boolean',
    label: 'Viewer-only mode: remove all other panels. Measurement and toolbar will still be available.',
  },
  {
    name: 'contentGridTemplate',
    type: 'textarea',
    label: 'Grid defining availability and size of UI panels. Uses CSS grid syntax. Only applied if Viewer-only mode is disabled.',
    helpComponent: GridHelp,
  },
  {
    name: 'displayMode',
    type: 'select',
    label: 'Display mode for visualizing based on SPARQL results',
    options: [
      {
        label: 'normal: show everything by default (no sparql necessary)',
        value: 'normal',
      },
      {
        label: 'isolate: show only sparql results, hide other elements',
        value: 'isolate',
      },
      {
        label: 'transparent: show only sparql results, make other elements transparent',
        value: 'transparent',
      }
    ]
  },
  {
    name: 'transparencyOpacity',
    type: 'number',
    label: 'Transparency opacity, relevant for display mode "transparent" and ghosted IFCs',
  },
  {
    name: 'assignRandomColorsToTags',
    type: 'boolean',
    label: 'Assigning random colors to tagged elements (ignored when a predefined color exists for the tag)',
  },
  {
    name: 'predefinedColors',
    type: 'json-text',
    label: <div>JSON-object linking ?tags to actual CSS-style colors. Example: <code>{JSON.stringify({ "Walls": "#3366ff", "Floors": "red" })}</code></div>
  },
  {
    name: 'legendSorting',
    type: 'select',
    label: 'Legend tag sorting',
    options: [
      {
        label: 'By element count',
        value: 'count',
      },
      {
        label: 'Alphabetical',
        value: 'alphabetical',
      },
      {
        label: 'As they appear in SPARQL',
        value: 'assignments-order',
      },
      {
        label: 'As they appear in predefined colors',
        value: 'predefined-colors-order',
      },
    ]
  },
  {
    name: 'ifcUrls',
    type: 'text',
    label: 'IFC URLs to load and view (not the UploadAnyFile URI)',
    topicValueInterpolationEnabled: true
  },
  {
    name: 'fragUrls',
    type: 'text',
    label: 'Fragment URLs to load and view (not the UploadAnyFile URI)',
    topicValueInterpolationEnabled: true
  },
  {
    name: 'selectionTopic',
    type: 'text',
    label: 'Topic to publish 3D selection changes to'
  },
  {
    name: 'selectionSubscription',
    type: 'text',
    label: 'Topic to listen to for selection and to apply in 3D'
  },
  {
    name: 'ghostedModelsSubscription',
    type: 'text',
    label: 'Topic to listen to for the URLs of models to ghost. When set, this replaces the ghosting configured in the triplestore, so the publisher (e.g. a Tree++ with checkboxes) owns the ghosting state for the session.'
  },
  {
    name: 'zoomOnSelectionChange',
    type: 'boolean',
    label: 'Automatically zoom in to selection when selection variable has changed.',
    formGroup: 'Selection',
  },
  {
    name: 'zoomToSelection',
    type: 'boolean',
    label: 'Automatically zoom in to selection when changing selection interactively.',
    formGroup: 'Selection',
  },
  {
    name: 'infinityDolly',
    type: 'boolean',
    label: 'Allow unlimited zoom distance. May cause the camera to enter an unrecoverable state in some cases.',
    formGroup: 'Camera controls',
  },
  {
    name: 'dollyToCursor',
    type: 'boolean',
    label: 'Zoom toward the cursor position. When off, always zooms towards the center.',
    formGroup: 'Camera controls',
  },
  {
    name: 'verticalDragToForward',
    type: 'boolean',
    label: 'Drag vertically to move forward/back instead of up/down. More suitable for wider, flatter models.',
    formGroup: 'Camera controls',
  },
  {
    name: 'minDollyDistance',
    type: 'number',
    label: 'Closest the camera can zoom in. Must be greater than 0.',
    formGroup: 'Camera controls',
  },
  {
    name: 'maxDollyDistance',
    type: 'number',
    label: 'Furthest the camera can zoom out.',
    formGroup: 'Camera controls',
  },
  {
    name: 'autoConvertToFrag',
    type: 'boolean',
    label: 'Automatically convert any IFCs that are loaded to Frag files. (Converting requires full-access permissions)',
    topicValueInterpolationEnabled: true
  },
  {
    name: 'autoConvertToLd',
    type: 'boolean',
    label: 'Automatically convert any Fragments that are loaded to LD files. IFCs will need to be converted to Fragments first. (Converting requires full-access permissions)',
    topicValueInterpolationEnabled: true
  },
  {
    name: 'ifcLdGraph',
    type: 'text',
    label: 'Target graph for IFC Linked Data. Use {{generated_uuid}} for random UUID. You can also use other app variables.'
  },
  {
    name: 'ifcToLdConfig',
    type: 'json-text',
    label: 'Configuration for JSON-to-LD',
    helpComponent: IfcToLdConfigExampleGenerator
  },
  {
    name: 'materialsDoubleSided',
    type: 'boolean',
    formGroup: 'Materials',
    label: 'Render both sides of faces. Fixes invisible/flipped geometry, but may increase flickering and cost extra performance on large models.'
  },
  {
    name: 'materialsAlphaHash',
    type: 'boolean',
    formGroup: 'Materials',
    label: 'Use alpha-hashed transparency. Avoids sorting artifacts of standard transparency at the cost of some visible grain/noise.'
  },
  {
    name: 'postproductionEnabled',
    type: 'boolean',
    formGroup: 'Postproduction',
    label: 'Enable postproduction. When off, the scene renders without edges, ambient occlusion or anti-aliasing.'
  },
  {
    name: 'postproductionStyle',
    type: 'select',
    formGroup: 'Postproduction',
    label: (
      <div>
        Postproduction style (OBF.PostproductionAspect). Colored surfaces, pen/line-art edges, or a combination.<br />
        <strong>Due to limitations pen styles cannot be used together with SPARQL colors</strong>
      </div>
    ),
    options: [
      { label: 'Color', value: 'color' },
      { label: 'Pen (line-art)', value: 'pen' },
      { label: 'Pen + shadows', value: 'pen-shadows' },
      { label: 'Color + pen', value: 'color-pen' },
      { label: 'Color + shadows', value: 'color-shadows' },
      { label: 'Color + pen + shadows', value: 'color-pen-shadows' },
    ]
  },
  {
    name: 'postproductionSmaa',
    type: 'boolean',
    formGroup: 'Postproduction',
    label: 'SMAA anti-aliasing for smoothing jagged edges.'
  },
  {
    name: 'postproductionGlossEnabled',
    type: 'boolean',
    formGroup: 'Postproduction',
    label: 'Gloss effect: makes surfaces appear more reflective depending on viewing angle.'
  },
  {
    name: 'ambientOcclusionConfig',
    type: 'json-text',
    formGroup: 'Postproduction',
    label: 'Configuration for ambient occlusion (GTAO + Poisson Denoise). Only applies when the selected style includes shadows.',
    helpComponent: AmbientOcclusionHelp
  },
  {
    name: 'edgesWidth',
    type: 'number',
    formGroup: 'Postproduction',
    label: 'Width of the detected edges, in pixels.'
  },
  {
    name: 'edgesColor',
    type: 'text',
    formGroup: 'Postproduction',
    label: <div>Color of the detected edges as a CSS color. Example: <code>#000000</code></div>
  },
  {
    name: 'edgesMode',
    type: 'select',
    formGroup: 'Postproduction',
    label: 'Edge detection mode. "Default" looks best (includes LODs); "Global" is faster but skips LODs.',
    options: [
      { label: 'Default (best looking)', value: 'default' },
      { label: 'Global (more performant)', value: 'global' },
    ]
  },
  {
    name: 'backgroundSkybox',
    type: 'select',
    formGroup: 'Background',
    label: (
      <div>
        Skybox image. When set, this overrides the CSS background below.<br />
        Images sourced from{' '}
        <a href="https://polyhaven.com"
           target="_blank" rel="noopener noreferrer"
           className="text-sky-600 underline hover:text-sky-700">polyhaven.com</a>{' '}
        [license: <a href="https://creativecommons.org/publicdomain/zero/1.0/"
           target="_blank" rel="noopener noreferrer"
           className="text-sky-600 underline hover:text-sky-700">CC0</a>]
      </div>
    ),
    options: [
      { label: 'None (use CSS background below)', value: '' },
      ...SKYBOX_PRESETS.map(preset => ({ label: preset.label, value: preset.id } as const)),
    ]
  },
  {
    name: 'backgroundCss',
    type: 'text',
    formGroup: 'Background',
    label: (
      <div>
        Viewer background, used when no skybox is selected above. Any valid CSS <code>background</code> value.
        Examples: <code>#1a1d23</code>, <code>linear-gradient(#3a3f4b, #1a1d23)</code>
      </div>
    )
  },
  {
    name: 'showGrid',
    type: 'boolean',
    label: 'Show the reference grid on the ground plane.'
  },
  PublishUtils.getMultipleValueFormatOptions(),
  PublishUtils.getTimestampParametersDef()
] as const

export type RawIFCViewerV4ConfigOptions = ConfigurablePropsFromDescription<typeof configOptions>

export type IFCViewerV4ConfigOptions = Override<RawIFCViewerV4ConfigOptions, {
  ifcToLdConfig?: IfcToLdConfig | null,
  predefinedColors?: Record<string, string>,
  ambientOcclusionConfig?: AmbientOcclusionConfig | null,
  _parsingErrors: Error[]
}>

export function parseAndSanitizeUrls(
  urlsValue: string | null | undefined,
  multipleselectparametertype: IFCViewerV4ConfigOptions["multipleselectparametertype"]
): string[] {
  if (!urlsValue) return []
  try {
    const rawUrls = PublishUtils.parseMultiplePropValue(multipleselectparametertype, urlsValue) as string[]
    return rawUrls.filter(url => Boolean(url) && url !== PublishUtils.resetUri)
  } catch (e) {
    console.error("Failed to parse ifc-viewer URLs", e)
    return []
  }
}

function IfcToLdConfigExampleGenerator() {
  const widgetId = useWidgetId()
  const [ example, setExample ] = React.useState<IfcToLdConfig>(EMPTY_IFC_TO_LD_CONFIG)

  async function generateExample() {
    const iframe = iframeConnectionsByWidgetId[widgetId]
    if (!iframe) {
      alert("Current IFC widget is not loaded or has broken IFrame connection")
      return
    }

    try {
      const example = await iframe.generateExampleIfcToLdConfig()
      if (!example) throw new Error("IFrame returned null example")
      setExample(example)
    } catch (e) {
      console.error(e)
      alert("Error generating example" + e)
    }
  }

  return (
    <div>
      <h1 className="text-lg font-bold mb-1">Example configuration</h1>
      <CodeEditor mode="json" readonly value={JSON.stringify(example, null, 4)} />

      <div className="my-2">
        <Button variant="default" onClick={generateExample}>
          Generate example based on data of currently loaded IFCs
        </Button>
      </div>

      <p>All properties are optional.</p>
      <p>Use <code>null</code> to export all attributes/categories/relations.
        But depending on the IFC, this may be slow and generate a lot of data.</p>
    </div>
  )
}
